import {
  CurrencyPipe,
  DatePipe,
  isPlatformBrowser,
  Location,
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  PLATFORM_ID,
  signal,
  untracked
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { SubscriptionLike } from 'rxjs';
import {
  Candidacy,
  VacancyDetail
} from '../../../../core/model/vacancies.model';
import { emulateClick } from '../core/a11y';
import { NotificationService } from '../notification/notification.service';
import { AutoCloseDirective } from '../shared/directives/auto-close.directive';
import { FileDropDirective } from '../shared/directives/file-drop.directive';
import { HoverGradientDirective } from '../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../shared/directives/progress-click.directive';
import { FlatchrService } from '../shared/services/flatchr.service';

type VacancyForm = {
  vacancy: FormControl<string>
  firstname: FormControl<string>
  lastname: FormControl<string>
  email: FormControl<string>
  phone: FormControl<string>
  resume: FormControl<{ data: string, fileName: string, contentType: string } | null>
  comment: FormControl<string>
  urls: FormGroup<{ linkedin: FormControl<string>, other: FormControl<string> }>
  answers: FormArray<FormGroup<{ question: FormControl<string>, value: FormControl<string> }>>,
  acceptPrivacy: FormControl<boolean>,
  acceptCGU: FormControl<boolean>,
}

@Component({
  selector: 'app-career',
  templateUrl: 'career.component.html',
  imports: [
    NgxTolgeeModule,
    HoverGradientDirective,
    NgOptimizedImage,
    DatePipe,
    CurrencyPipe,
    AutoCloseDirective,
    NgClass,
    NgClass,
    ReactiveFormsModule,
    ReactiveFormsModule,
    ProgressClickDirective,
    FileDropDirective,
    FileDropDirective,
    ReactiveFormsModule
  ]
})
export class CareerComponent {
  location = inject(Location);
  router = inject(Router);
  notification = inject(NotificationService);
  flatchrService = inject(FlatchrService);


  readonly EXPERIENCES: Record<number, string> = {
    1: 'Débutant(e)',
    2: 'Junior',
    4: 'Intermédiaire',
    6: 'Confirmé',
    11: 'Sénior'
  };
  spontaneous = signal<VacancyDetail | null>(null);
  vacancies = signal<VacancyDetail[] | null>(null);
  locations = computed(() => {
    const vacancies = [ ...new Set(this.vacancies()
      ?.map(v => v.tags[0]?.title)
      .filter(v => !!v)) ?? [] ];
    vacancies.sort((a, b) => a.localeCompare(b));
    return [ 'TOUT',
      ...vacancies ]
  });
  selectedLocation = signal('TOUT');
  contractTypes = computed(() => {
    const vacancies = [ ...new Set(this.vacancies()
      ?.map(v => v.contract_type)
      .filter(v => !!v)) ?? [] ];
    vacancies.sort((a, b) => b.localeCompare(a));
    return [ 'TOUT',
      ...vacancies ]
  });
  selectedContractType = signal('TOUT');
  jobs = computed(() => {
    const vacancies = [ ...new Set(this.vacancies()
      ?.map(v => v.metier)
      .filter(v => !!v)) ?? [] ];
    vacancies.sort((a, b) => a.localeCompare(b));
    return [ 'TOUT',
      ...vacancies ]
  });
  selectedJob = signal('TOUT');
  filter = signal('');
  displayedVacancies = computed(() => {
    const vacancies = this.vacancies();
    if (!vacancies) {
      return null;
    }
    const location = this.selectedLocation();
    const contractType = this.selectedContractType();
    const job = this.selectedJob();
    const filter = this.filter().toLowerCase().trim();
    return vacancies
      .filter(v => location === 'TOUT' || v.tags[0]?.title === location)
      .filter(v => contractType === 'TOUT' || v.contract_type === contractType)
      .filter(v => job === 'TOUT' || v.metier === job)
      .filter(v => !filter || v.title.toLowerCase().includes(filter))
  })
  selectedVacancy = signal<VacancyDetail | null>(null);
  showForm = signal(false)
  form: FormGroup<VacancyForm> | null = null;
  questions = signal<{
    id: number,
    text: string,
    formGroup: FormGroup,
    values: string[]
  }[]>([]);
  protected readonly emulateClick = emulateClick;
  protected readonly Object = Object;
  private locationSub: SubscriptionLike | null = null;

  constructor() {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      this.flatchrService.list()
        .then(([ spontaneous, vacancies ]) => {
          this.vacancies.set(vacancies);
          this.spontaneous.set(spontaneous);
        })
      effect(() => {
        window.scrollTo(0, 0);
        const vacancy = this.selectedVacancy();
        if (!vacancy) {
          this.form = null;
          this.locationSub?.unsubscribe();
          return;
        }
        const spontaneous = untracked(() => this.spontaneous());
        if (vacancy.vacancy_id === spontaneous?.vacancy_id) {
          this.showForm.set(true);
        }
        history.pushState({ id: 'current' }, '', window.location.href);
        this.locationSub = this.location.subscribe(event => {
          if (event.type === 'popstate') {
            this.backToList(false);
          }
        })
        const questions: {
          id: number,
          text: string,
          formGroup: FormGroup<{ question: FormControl<string>, value: FormControl<string> }>,
          values: string[]
        }[] = [];

        for (const question of vacancy.questions) {
          const formGroup = new FormGroup({
            question: new FormControl(question.text, { nonNullable: true }),
            value: new FormControl('', {
              nonNullable: true,
              validators: question.required ? [ Validators.required ] : []
            })
          })
          questions.push({
            id: question.id,
            text: question.text,
            formGroup,
            values: question.options.options.split(';')
          })
        }
        this.questions.set(questions);

        this.form = new FormGroup({
          vacancy: new FormControl(vacancy.slug, { nonNullable: true }),
          firstname: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
          lastname: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
          email: new FormControl('', { nonNullable: true, validators: [ Validators.required, Validators.email ] }),
          phone: new FormControl('', { nonNullable: true, validators: [ Validators.pattern(/^0[67]\d{8}$/) ] }),
          resume: new FormControl<{
            data: string,
            fileName: string,
            contentType: string
          } | null>(null, { nonNullable: true, validators: [ Validators.required ] }),
          comment: new FormControl('', { nonNullable: true }),
          urls: new FormGroup({
            linkedin: new FormControl('', { nonNullable: true }),
            other: new FormControl('', { nonNullable: true }),
          }),
          answers: new FormArray(questions.map(q => q.formGroup)),
          acceptPrivacy: new FormControl(false, {
            nonNullable: true,
            validators: [ Validators.required, Validators.requiredTrue ]
          }),
          acceptCGU: new FormControl(false, {
            nonNullable: true,
            validators: [ Validators.required, Validators.requiredTrue ]
          })
        })
      })
    }
  }

  get resumeControl(): FormControl<{ data: string, fileName: string, contentType: string }> {
    return this.form!.get('resume') as FormControl<{ data: string, fileName: string, contentType: string }>;
  }

  onFileChanged(event: any): void {
    const file: File = event.target.files[0];
    const maxSize = 10 * 1024 * 1024;
    if (file) {
      if (file.size > maxSize) {
        return
      }
      this.onCvChanged(file);
    }
  }

  onCvChanged(file: File): void {
    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      return
    }

    if (file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = (e): void => {
        this.form!.get('resume')!.setValue({
          contentType: file.type,
          fileName: file.name,
          data: e.target?.result as string
        });
      };

      // Read the file as a data URL (which includes the Base64 encoded string)
      reader.readAsDataURL(file);
    }
  }

  candidate = async (): Promise<void> => {
    const candidacy = this.form!.getRawValue() as Candidacy;
    try {
      await this.flatchrService.candidate(candidacy);
      this.backToList();
      this.notification.open({ type: 'success', title: 'vacancySuccess', message: 'vacancySuccessDescription' });
    } catch (err) {
      this.notification.open({ type: 'error', title: 'vacancyError', message: 'vacancyErrorDescription' });
    }
  };

  backToList(popHistory = true): void {
    if (popHistory) {
      history.back();
    }
    this.selectedVacancy.set(null);
    this.showForm.set(false);
    this.form = null
  }

  questionValueFormControl(formGroup: FormGroup): FormControl<string> {
    return formGroup.get('value') as FormControl<string>
  }

  displayForm(): void {
    this.showForm.set(true)
    window.scrollTo(0, 0);
  }

  getImage(metier: string): string {
    switch (metier) {
    case 'Gestionnaire de site':
      return 'assets/images/jobs/job_1.webp';
    case 'Vendeur':
      return 'assets/images/jobs/job_2.webp';
    case 'Agent commercial':
      return 'assets/images/jobs/job_3.webp';
    case 'Agent - assistance aux personnes':
      return 'assets/images/jobs/job_4.webp';
    case 'Responsable logistique':
      return 'assets/images/jobs/job_5.webp';
    case 'Commercial conseil':
      return 'assets/images/jobs/job_6.webp';
    case 'Administrateur des ventes':
      return 'assets/images/jobs/job_7.webp';


    case 'Directeur Centre':
      return 'assets/images/jobs/job_1.webp';
    case 'Responsable Bains':
      return 'assets/images/jobs/job_2.webp';
    case 'Fonctions support':
      return 'assets/images/jobs/job_4.webp';
    case 'SPA manager':
      return 'assets/images/jobs/job_5.webp';
    case 'Chargée d\'accueil':
      return 'assets/images/jobs/job_6.webp';
    case 'Responsable Accueil':
      return 'assets/images/jobs/job_7.webp';
    case 'Spa praticienne':
      return 'assets/images/jobs/job_8.webp';
    case 'Responsable Accueils et SPA':
      return 'assets/images/jobs/job_9.webp';
    case 'Surveillant de bains (BNSSA)':
      return 'assets/images/jobs/job_10.webp';
    case 'Animateur de bains (MNS)':
      return 'assets/images/jobs/job_11.webp';
    default:
      return 'assets/images/jobs/job_3.webp';
    }
  }
}
